import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { safeAdminError } from '../safe-ui'

type HistoryRow = {
  id: number
  createdAt?: string
  action: string
  amount: number
  balanceBefore?: string | number | null
  balanceAfter?: string | number | null
  reason?: string
  reference?: string
  accountId?: number
  clientId?: number
  clientEmail?: string
  clientName?: string
  subadminId?: number | null
  subadminCode?: string
  subadminName?: string
  mt5Login?: number
  mt5LoginMasked?: string
  mode?: string
}

function includes(hay: string | number | null | undefined, needle: string) {
  if (!needle.trim()) return true
  return String(hay ?? '')
    .toLowerCase()
    .includes(needle.trim().toLowerCase())
}

export function AdminBalancePage() {
  const [mt5Login, setMt5Login] = useState('')
  const [action, setAction] = useState<'CREDIT' | 'DEBIT'>('CREDIT')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [preview, setPreview] = useState<{
    mt5Login?: number
    mt5LoginMasked?: string
    mode?: string
    clientId?: number
    clientEmail?: string
    clientName?: string
    subadminId?: number | null
    subadminCode?: string
    subadminName?: string
    accountId?: number
  } | null>(null)

  const [filterDate, setFilterDate] = useState('')
  const [filterAction, setFilterAction] = useState('')
  const [filterMt5, setFilterMt5] = useState('')
  const [filterClient, setFilterClient] = useState('')
  const [filterIb, setFilterIb] = useState('')
  const [rows, setRows] = useState<HistoryRow[]>([])
  const [histLoading, setHistLoading] = useState(true)

  function formatDateOnly(value: string) {
    if (!value) return '—'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
    return d.toLocaleDateString()
  }

  function toLocalYmd(value: string) {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const loadHistory = useCallback(async () => {
    setHistLoading(true)
    try {
      const data = await adminApi.balanceHistory({ limit: 200 })
      setRows(data.items || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load balance history'))
    } finally {
      setHistLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterDate && toLocalYmd(String(r.createdAt || '')) !== filterDate) return false
      if (filterAction && String(r.action || '').toUpperCase() !== filterAction) return false
      if (!includes(r.mt5Login ?? r.mt5LoginMasked, filterMt5)) return false
      if (!includes(`${r.clientName || ''} ${r.clientId ?? ''} ${r.clientEmail || ''}`, filterClient))
        return false
      if (!includes(`${r.subadminCode || ''} ${r.subadminName || ''} ${r.subadminId ?? ''}`, filterIb))
        return false
      return true
    })
  }, [rows, filterDate, filterAction, filterMt5, filterClient, filterIb])

  function clearForm() {
    setMt5Login('')
    setAction('CREDIT')
    setAmount('')
    setReason('')
    setPreview(null)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    setPreview(null)
    const login = Number(mt5Login)
    const value = Number(amount)
    if (!Number.isFinite(login) || login <= 0) {
      setError('Enter a valid MT5 login ID')
      return
    }
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a valid amount')
      return
    }
    if (reason.trim().length < 3) {
      setError('Enter a reason (min 3 characters)')
      return
    }
    setBusy(true)
    try {
      const data = await adminApi.balanceLookup(login)
      setPreview(data.account || null)
      setConfirmOpen(true)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load account for this MT5 login'))
    } finally {
      setBusy(false)
    }
  }

  async function runAdjust() {
    setBusy(true)
    setError('')
    setNotice('')
    setConfirmOpen(false)
    try {
      const res = await adminApi.balanceAdjust({
        action,
        amount: Number(amount),
        reason: reason.trim(),
        mt5Login: Number(mt5Login),
      })
      setNotice(
        `${res.action} $${Number(res.amount).toFixed(2)} on ${res.mt5LoginMasked || mt5Login} · ${res.balanceBefore ?? '—'} → ${res.balanceAfter ?? '—'}`,
      )
      clearForm()
      await loadHistory()
    } catch (err) {
      setError(safeAdminError(err, 'Balance update failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Master Admin" title="MT5 balance add / withdraw">
        <p className="cp-muted">
          Credits go to MT5 Balance (not Credit). Comment: <code>Admin CREDIT/DEBIT: reason</code>
        </p>
      </PageHeader>

      {error ? (
        <StateBlock tone="error" title="Error">
          {error}
        </StateBlock>
      ) : null}
      {notice ? (
        <StateBlock tone="info" title="Done">
          {notice}
        </StateBlock>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(16rem, 20rem) minmax(0, 1fr)',
          gap: '1.25rem',
          alignItems: 'start',
        }}
        className="ap-balance-layout"
      >
        <form
          className="cp-panel cp-auth__form"
          onSubmit={(e) => void onSubmit(e)}
          style={{ padding: '1rem 1.1rem', margin: 0 }}
        >
          <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>Quick adjust</h2>
          <label className="cp-field">
            <span className="cp-field__label">MT5 login</span>
            <input
              inputMode="numeric"
              value={mt5Login}
              onChange={(e) => setMt5Login(e.target.value.trim())}
              placeholder="100234"
              required
            />
          </label>
          <label className="cp-field">
            <span className="cp-field__label">Action</span>
            <select value={action} onChange={(e) => setAction(e.target.value as 'CREDIT' | 'DEBIT')}>
              <option value="CREDIT">Add (CREDIT)</option>
              <option value="DEBIT">Withdraw (DEBIT)</option>
            </select>
          </label>
          <label className="cp-field">
            <span className="cp-field__label">Amount USD</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </label>
          <label className="cp-field">
            <span className="cp-field__label">Reason</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ops note"
              required
              minLength={3}
            />
          </label>
          <Button type="submit" disabled={busy} size="sm">
            {busy ? '…' : 'Review'}
          </Button>
        </form>

        <section className={`cp-panel${histLoading ? ' is-refreshing' : ''}`} style={{ margin: 0, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '0.75rem',
            }}
          >
            <h2 style={{ margin: 0 }}>Balance history</h2>
            <Button type="button" variant="secondary" size="sm" onClick={() => void loadHistory()}>
              Refresh
            </Button>
          </div>

          {histLoading ? (
            <p className="cp-muted ap-inline-status" aria-live="polite">
              Loading…
            </p>
          ) : null}

          <div className="pp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Amount</th>
                  <th>MT5</th>
                  <th>Client</th>
                  <th>IB / Sub</th>
                </tr>
                <tr className="ap-filter-row">
                  <th>
                    <input
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      aria-label="Filter date"
                      title="Select date"
                    />
                  </th>
                  <th>
                    <select
                      value={filterAction}
                      onChange={(e) => setFilterAction(e.target.value)}
                      aria-label="Filter action"
                    >
                      <option value="">All</option>
                      <option value="CREDIT">CREDIT</option>
                      <option value="DEBIT">DEBIT</option>
                    </select>
                  </th>
                  <th />
                  <th>
                    <input
                      value={filterMt5}
                      onChange={(e) => setFilterMt5(e.target.value)}
                      placeholder="MT5"
                      aria-label="Filter MT5"
                    />
                  </th>
                  <th>
                    <input
                      value={filterClient}
                      onChange={(e) => setFilterClient(e.target.value)}
                      placeholder="Name / id"
                      aria-label="Filter client"
                    />
                  </th>
                  <th>
                    <input
                      value={filterIb}
                      onChange={(e) => setFilterIb(e.target.value)}
                      placeholder="IB code / name"
                      aria-label="Filter IB"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {histLoading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="cp-muted">
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="cp-muted">
                      No matching balance entries.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                      <tr key={r.id}>
                        <td>{formatDateOnly(String(r.createdAt || ''))}</td>
                        <td>{r.action}</td>
                        <td>${Number(r.amount || 0).toFixed(2)}</td>
                        <td>
                          {r.accountId ? (
                            <Link to={`/admin/trading-accounts/${r.accountId}`}>
                              {r.mt5Login ?? r.mt5LoginMasked ?? '—'}
                            </Link>
                          ) : (
                            String(r.mt5Login ?? r.mt5LoginMasked ?? '—')
                          )}
                        </td>
                        <td>
                          {r.clientId ? (
                            <Link to={`/admin/clients/${r.clientId}`}>
                              {r.clientName || '—'}
                              {r.clientId != null ? ` (#${r.clientId})` : ''}
                            </Link>
                          ) : (
                            <>
                              {r.clientName || '—'}
                              {r.clientId != null ? ` (#${r.clientId})` : ''}
                            </>
                          )}
                        </td>
                        <td>
                          {!r.subadminCode && !r.subadminName
                            ? '—'
                            : `${[r.subadminCode, r.subadminName].filter(Boolean).join(' · ')}${
                                r.subadminId != null ? ` (#${r.subadminId})` : ''
                              }`}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <style>{`
        .ap-filter-row th {
          padding-top: 0.35rem;
          padding-bottom: 0.65rem;
          vertical-align: top;
          font-weight: 400;
        }
        .ap-filter-row input,
        .ap-filter-row select {
          width: 100%;
          min-width: 4.5rem;
          max-width: 9rem;
          box-sizing: border-box;
          font: inherit;
          font-size: 0.85rem;
          padding: 0.3rem 0.4rem;
        }
        .ap-filter-row input[type='date'] {
          max-width: 11rem;
          min-width: 8.5rem;
          color-scheme: dark;
        }
        @media (max-width: 960px) {
          .ap-balance-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <ConfirmDialog
        open={confirmOpen}
        title={`${action === 'CREDIT' ? 'Add' : 'Withdraw'} $${Number(amount || 0).toFixed(2)}?`}
        body={
          <div style={{ display: 'grid', gap: '0.35rem' }}>
            <p style={{ margin: 0 }}>
              <strong>MT5:</strong> {preview?.mt5Login ?? mt5Login}
              {preview?.mode ? ` · ${preview.mode}` : ''}
            </p>
            <p style={{ margin: 0 }}>
              <strong>Email:</strong> {preview?.clientEmail || '—'}
            </p>
            <p style={{ margin: 0 }}>
              <strong>Client:</strong> {preview?.clientName || '—'}
              {preview?.clientId != null ? ` (#${preview.clientId})` : ''}
            </p>
            <p style={{ margin: 0 }}>
              <strong>IB / Sub:</strong>{' '}
              {preview?.subadminCode || preview?.subadminName
                ? [preview?.subadminCode, preview?.subadminName].filter(Boolean).join(' · ')
                : '—'}
              {preview?.subadminId != null ? ` (#${preview.subadminId})` : ''}
            </p>
            <p className="cp-muted" style={{ margin: '0.35rem 0 0' }}>
              Reason: {reason.trim()} · Posts to MT5 Balance via bridge.
            </p>
          </div>
        }
        confirmLabel={action === 'CREDIT' ? 'Credit now' : 'Debit now'}
        danger={action === 'DEBIT'}
        busy={busy}
        onCancel={() => {
          setConfirmOpen(false)
          setPreview(null)
        }}
        onConfirm={() => void runAdjust()}
      />
    </section>
  )
}
